import React, { FC, useContext, useMemo, useState, useCallback } from "react";
import "./TaskChecklistElements.css";
import DragDropGray from "../../../assets/dragDrop.svg";
import EditColumnGray from "../../../assets/editButtonGray.svg";
import ExitCross from "../../../assets/cancelGreey.svg";
import { AppContext } from "../../../App";
import { Context } from "../../../index";
import { IFunctionPermissions } from "../../../common/api_interfaces/role/IFunctionPermissions";
// import { ITaskChecklist } from "../../../models/TaskModels/ITaskChecklist";
import Checkbox from "../../Shared/Checkbox/Checkbox";
import DeleteElementGray from "../../../assets/deleteElementGreyTrue.svg";
import SaveInputValue from "../../../assets/save_input_value.svg";

import ArrowButton from "../../buttons/ArrowButton/ArrowButton";
import InputChat from "../../Shared/InputChat/InputChat";
import InfoPanel from "../../LeftMenu/InfoPanel/InfoPanel";
import axios from "axios";
// import { ITaskChecklistGroup } from "../../../models/TaskModels/ITaskChecklistGroup";
import ConfirmationWindow from "../../Shared/ConfirmationWindow/ConfirmationWindow";
import { hasPermission } from "../../../helpers/hasPermisson";
import { IOptionsQueryParams } from "../../../helpers/paramsAutoFiller";
import { TaskContext } from "../../../contexts/TaskContext";
import { ITaskChecklist } from "../../../common/api_interfaces/taskChecklist/ITaskChecklist";
import { ITaskChecklistGroup } from "../../../common/api_interfaces/taskChecklist/ITaskChecklistGroup";

interface EditChecklist {
    checklistElemntId: number;
    newText?: string;
    newPosition?: number;
    newChecked?: boolean;
}

interface ITaskChecklistElementsProps {
    checklistElement: ITaskChecklist;
    onChecklistElementDragEnd: () => void;
    draggable: boolean;
    systemUserAccess: IFunctionPermissions[];
    userAccess: IFunctionPermissions[];
    editElement: (data: EditChecklist) => Promise<void>;
    loadChecklist: () => Promise<void>;
    openAllDropdown?: boolean;
    setIsLowerElementDrag: React.Dispatch<React.SetStateAction<boolean>>;
    setChecklistGroup: React.Dispatch<React.SetStateAction<ITaskChecklistGroup | undefined>>;
    checkListGroup: ITaskChecklistGroup;
    optionsQueryParams: IOptionsQueryParams;
}

const TaskChecklistElements: FC<ITaskChecklistElementsProps> = ({
    setIsLowerElementDrag,
    setChecklistGroup,
    checklistElement,
    onChecklistElementDragEnd,
    draggable,
    systemUserAccess,
    userAccess,
    editElement,
    loadChecklist,
    openAllDropdown,
    checkListGroup,
    optionsQueryParams,
}) => {
    const { store } = useContext(Context);
    const { showToast } = useContext(AppContext);
    const { taskInformation } = useContext(TaskContext);
    
    const canEditChecklist = useMemo(() => 
        hasPermission(systemUserAccess, "ChecklistAction", "edit") || hasPermission(userAccess, "ChecklistAction", "edit"), 
        [userAccess, systemUserAccess]
    );

    const canDeleteChecklist = useMemo(() => 
        hasPermission(systemUserAccess, "ChecklistAction", "delete") || hasPermission(userAccess, "ChecklistAction", "delete"), 
        [userAccess]
    );
    
    const [renameMode, setRenameMode] = useState(false);
    const [openMessages, setOpenMessages] = useState(openAllDropdown ?? false);
    const [messagesCount, setMessagesCount] = useState(0);
    const [openConfirmWindow, setOpenConfirmWindow] = useState(false);
    const [newChecked, setNewChecked] = useState<boolean>();
    const [newText, setNewText] = useState<string>();
    const [showGroupBtn, setShowGroupBtn] = useState<number>();

    const { id, text, checked, chatId } = checklistElement;

    const onCancel = useCallback(() => {
        setOpenConfirmWindow(false);
    }, []);

    const onDragStart = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        setIsLowerElementDrag(true);
        setChecklistGroup(checkListGroup);
        
        e.stopPropagation();
        setTimeout(() => {
            const checklistElement = (e.target as HTMLElement).closest(".task_checklist_elem");
            if (checklistElement) {
                checklistElement.classList.add("drag-hidden");
            }
        }, 0);
        e.dataTransfer.clearData();
        e.dataTransfer.setData(
            "application/json",
            JSON.stringify({
                checklistElementId: id,
                checklistGroupId: checkListGroup.id,
            })
        );
    }, [setIsLowerElementDrag, setChecklistGroup, checkListGroup, id]);

    const onDragEnd = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        setChecklistGroup(undefined);
        setIsLowerElementDrag(false);
        
        const taskContainers = document.querySelectorAll(".task_checklist_element");
        taskContainers.forEach((taskContainer) => {
            const existingContainer = taskContainer.querySelector(".task_checklist_elem_container.preview-container");
            existingContainer?.remove();
        });

        const checklistElement = (e.target as HTMLElement).closest(".task_checklist_elem");
        if (checklistElement) {
            setTimeout(() => {
                checklistElement.classList.remove("drag-hidden");
            }, 100);
        }
        
        onChecklistElementDragEnd();
    }, [setIsLowerElementDrag, onChecklistElementDragEnd]);

    
    const saveTextChange = useCallback(() => {
        if (newText?.length && newText !== text) {
            editElement({
                checklistElemntId: id,
                newText: newText,
            });
        }
        setRenameMode(false);
    }, [newText, text, id, editElement]);
    
    const handleNameChange = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            saveTextChange();
        } else if (e.key === "Escape") {
            setNewText(text);
            setRenameMode(false);
        }
    }, [text, saveTextChange]);

    const inputNewText = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setNewText(e.target.value);
    }, []);

    const handleSelectionChecklistElement = useCallback((id: number, e: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = e.target.checked;
        setNewChecked(isChecked);
        editElement({
            checklistElemntId: id,
            newChecked: isChecked,
        });
    }, [editElement]);

    const handleDeleteChecklistElement = useCallback(async (elementId: number) => {
        try {
            await store.taskService.deleteTaskChecklistElement(Number(elementId));
            await loadChecklist();
        } catch (error) {
            if (axios.isAxiosError(error)) {
                showToast(error.response?.data?.message);
            }
        }
    }, [store.taskService, loadChecklist, showToast]);

    const showGroupsList = useCallback((elementId: number) => {
        setShowGroupBtn(elementId);
    }, []);

    const hideGroupsList = useCallback(() => {
        setShowGroupBtn(undefined);
    }, []);

    const toggleRenameMode = useCallback(() => {
        if (canEditChecklist) {
            setRenameMode(prev => !prev);
        } else {
            showToast("У вас нет прав для редактирования пункта!");
        }
    }, [canEditChecklist, showToast]);

    const toggleMessages = useCallback(() => {
        setOpenMessages(prev => !prev);
    }, []);

    const requestDelete = useCallback(() => {
        if (canDeleteChecklist) {
            setOpenConfirmWindow(true);
        } else {
            showToast("У вас нет прав для удаления пункта!");
        }
    }, [canDeleteChecklist, showToast]);

    const isChecked = newChecked !== undefined ? newChecked : checked;
    const displayText = newText ?? text;
    const shouldShowButtons = showGroupBtn === id;

    return (
        <div className="task_checklist_elem_container">
            {openConfirmWindow && (
                <ConfirmationWindow
                    headerText="Удаление пункта"
                    bodyText="Вы уверены, что хотите удалить пункта?"
                    confirmText="Да"
                    cancelText="Нет"
                    onConfirm={() => handleDeleteChecklistElement(id)}
                    onCancel={onCancel}
                />
            )}
            
            <div
                id={`element-${id}`}
                className={`task_checklist_elem ${renameMode ? "renameMode" : ""}`}
                onMouseLeave={hideGroupsList}
                onMouseEnter={() => showGroupsList(id)}
            >
                <div
                    className="drag-button"
                    draggable={draggable && canEditChecklist}
                    onDragStart={onDragStart}
                    onDragEnd={onDragEnd}
                >
                    <div className="dragDrop__edit">
                        {canEditChecklist && draggable && <img src={DragDropGray} alt="Drag" />}
                    </div>
                </div>
                
                <InfoPanel count={messagesCount} idKey={String(id)} />
                
                <ArrowButton
                    onClick={toggleMessages}
                    direction={openMessages ? "bottom" : "right"}
                />
                
                <Checkbox
                    id={id}
                    handleSelection={handleSelectionChecklistElement}
                    checked={isChecked}
                    color="gray"
                />

                <div className="task_checklist_elem__block">
                    {renameMode ? (
                        <div className="task_checklist_elem__block--name">
                            <input
                                type="text"
                                defaultValue={text}
                                onKeyDown={handleNameChange}
                                onChange={inputNewText}
                                autoFocus
                            />
                        </div>
                    ) : (
                        <div
                            className={`task_checklist_elem__block--name ${isChecked ? "checked" : ""}`}
                            onClick={toggleMessages}
                        >
                            {displayText}
                        </div>
                    )}
                    
                    {shouldShowButtons && (
                        <div className="task_checklist_elem__block__button">
                            <button onClick={toggleRenameMode}>
                                <img src={renameMode ? ExitCross : EditColumnGray} alt={renameMode ? "Cancel" : "Edit"} />
                            </button>
                            
                            {!renameMode && (
                                <button onClick={requestDelete}>
                                    <img src={DeleteElementGray} alt="Delete" />
                                </button>
                            )}
                            
                            {renameMode && (
                                <button className="postive-green-button" onClick={saveTextChange}>
                                    <img src={SaveInputValue} alt="Save" />
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
            
            <div style={{ display: openMessages ? "block" : "none" }}>
                <InputChat
                    userAccess={userAccess}
                    chatId={chatId}
                    setMessagesCount={setMessagesCount}
                    projectId={taskInformation?.projectId}
                    messageUrlQueryParams="tab=1&checkList=1"
                    optionsQueryParams={{
                        page: optionsQueryParams.page,
                        entity: optionsQueryParams.entity,
                        action: optionsQueryParams.action,
                        entityId: optionsQueryParams.entityId,
                        cheklistId: optionsQueryParams.cheklistId,
                    }}
                />
            </div>
        </div>
    );
};

export default React.memo(TaskChecklistElements);